import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { CalendarPlus, ChevronDown, Check, X, Trash2, MessageSquareHeart, Copy, Mail, StickyNote, Trophy } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const localNow = () => { const d = new Date(Date.now() + 864e5); d.setMinutes(0); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`; };

export function ClubTimetable() {
  const qc = useQueryClient();
  const { data: classes = [] } = useQuery({ queryKey: ["club-classes"], queryFn: async () => (await api.get("/api/club/classes")).data });
  const [form, setForm] = useState({ title: "", starts_at: localNow(), duration_min: 60, capacity: 12, location: "", repeat_weeks: 4, description: "" });
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const create = async () => {
    if (!form.title.trim()) { toast.error("Give the class a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/club/classes", { ...form, starts_at: new Date(form.starts_at).toISOString(),
        duration_min: Number(form.duration_min), capacity: Number(form.capacity), repeat_weeks: Number(form.repeat_weeks),
        tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; } })() });
      toast.success(`${data.created.length} session${data.created.length > 1 ? "s" : ""} added. Your athletes can book now.`);
      setForm({ ...form, title: "", description: "" });
      qc.invalidateQueries({ queryKey: ["club-classes"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const mark = async (cid, uid, attended) => {
    try { await api.post(`/api/club/classes/${cid}/attendance`, { user_id: uid, attended }); qc.invalidateQueries({ queryKey: ["club-classes"] }); qc.invalidateQueries({ queryKey: ["club"] }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (cid) => {
    try { await api.delete(`/api/club/classes/${cid}`); toast("Class removed"); qc.invalidateQueries({ queryKey: ["club-classes"] }); } catch (e) { toast.error(apiErr(e)); }
  };
  const now = new Date().toISOString();
  const upcoming = classes.filter((c) => c.starts_at >= now);
  const past = classes.filter((c) => c.starts_at < now).reverse();

  const Row = ({ c }) => {
    const d = new Date(c.starts_at);
    const isOpen = open === c.id;
    return (
      <div className="rounded-lg border border-[#1E222A] bg-[#0B0D11]">
        <button onClick={() => setOpen(isOpen ? null : c.id)} className="w-full flex items-center gap-3 p-3 text-left">
          <div className="w-14 text-center shrink-0">
            <p className="mono-label text-acid">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
            <p className="font-heading font-black">{d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</p>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{c.title}</p>
            <p className="text-xs text-[#8A91A6]">{d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} · {c.duration_min} min · {c.location}</p>
          </div>
          <span className="text-sm text-[#B7BECB]">{c.booked_count}/{c.capacity}{c.starts_at < now ? ` · ${c.attended_count} came` : ""}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className="px-3 pb-3 space-y-2">
            {c.roster.length === 0 && <p className="text-sm text-[#565C6F]">No bookings yet.</p>}
            {c.roster.map((r) => (
              <div key={r.user_id} className="flex items-center gap-2 text-sm">
                <span className="flex-1">{r.name}</span>
                <button onClick={() => mark(c.id, r.user_id, true)} className={`h-8 px-3 rounded-full border text-xs inline-flex items-center gap-1 ${r.attended === true ? "bg-acid text-black border-acid" : "border-[#343A4A]"}`}><Check className="w-3.5 h-3.5" /> Came</button>
                <button onClick={() => mark(c.id, r.user_id, false)} className={`h-8 px-3 rounded-full border text-xs inline-flex items-center gap-1 ${r.attended === false ? "bg-[#FF6B6B] text-black border-[#FF6B6B]" : "border-[#343A4A]"}`}><X className="w-3.5 h-3.5" /> No-show</button>
              </div>
            ))}
            <button onClick={() => del(c.id)} className="text-xs text-[#8A91A6] hover:text-[#FF6B6B] inline-flex items-center gap-1 mt-1"><Trash2 className="w-3.5 h-3.5" /> Remove this session</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5" data-testid="club-timetable">
      <p className="font-heading font-bold text-lg">Club timetable</p>
      <p className="text-sm text-[#8A91A6]">Run your own classes. Only your athletes see and book them, and attendance counts towards their training diary.</p>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-4">
        <input value={form.title} onChange={set("title")} placeholder="Class name, e.g. Tuesday Strength" className="md:col-span-2 h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
        <input type="datetime-local" value={form.starts_at} onChange={set("starts_at")} className="md:col-span-2 h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
        <input type="number" value={form.duration_min} onChange={set("duration_min")} title="Minutes" className="h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
        <input type="number" value={form.capacity} onChange={set("capacity")} title="Places" className="h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
        <input value={form.location} onChange={set("location")} placeholder="Where (optional)" className="md:col-span-2 h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
        <select value={form.repeat_weeks} onChange={set("repeat_weeks")} className="md:col-span-2 h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm">
          {[1, 4, 8, 12].map((n) => <option key={n} value={n}>{n === 1 ? "One-off" : `Every week for ${n} weeks`}</option>)}
        </select>
        <Button onClick={create} disabled={busy} className="md:col-span-2 h-10 bg-acid text-black hover:bg-[#AACC00]"><CalendarPlus className="w-4 h-4" /> Add to timetable</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
        <div><p className="mono-label mb-2">upcoming</p><div className="space-y-2">{upcoming.slice(0, 10).map((c) => <Row key={c.id} c={c} />)}{!upcoming.length && <p className="text-sm text-[#565C6F]">Nothing scheduled.</p>}</div></div>
        <div><p className="mono-label mb-2">recent · mark attendance</p><div className="space-y-2">{past.slice(0, 10).map((c) => <Row key={c.id} c={c} />)}{!past.length && <p className="text-sm text-[#565C6F]">No past sessions yet.</p>}</div></div>
      </div>
    </div>
  );
}

export function ClubNudges() {
  const { data, isFetching, refetch } = useQuery({ queryKey: ["club-nudges"], queryFn: async () => (await api.get("/api/club/nudges")).data, enabled: false });
  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Message copied"); } catch (_) {} };
  return (
    <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5" data-testid="club-nudges">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-heading font-bold text-lg flex items-center gap-2"><MessageSquareHeart className="w-5 h-5 text-acid" /> Bring athletes back</p>
          <p className="text-sm text-[#8A91A6]">Your OCTON coach drafts a personal message for each athlete who has gone quiet for a week or more.</p>
        </div>
        <Button onClick={() => refetch()} disabled={isFetching} className="bg-acid text-black hover:bg-[#AACC00] rounded-full">{isFetching ? "Writing…" : data ? "Refresh" : "Draft messages"}</Button>
      </div>
      {data && (
        <div className="mt-4 space-y-2">
          {data.nudges.length === 0 && <p className="text-sm text-[#8A91A6]">Everyone has trained this week. Nice.</p>}
          {data.nudges.map((n) => (
            <div key={n.user_id} className="rounded-lg border border-[#1E222A] bg-[#0B0D11] p-3">
              <div className="flex items-center justify-between text-sm"><b>{n.name}</b><span className="mono-label">{n.days_since == null ? "not started" : `${n.days_since} days quiet`}</span></div>
              <p className="text-sm text-[#D7DCE5] mt-1">{n.message}</p>
              <div className="flex gap-3 mt-2 text-xs">
                <button onClick={() => copy(n.message)} className="text-acid inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button>
                {n.email && <a href={`mailto:${n.email}?subject=${encodeURIComponent("Missing you at training")}&body=${encodeURIComponent(n.message)}`} className="text-acid inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AthleteDrawer({ athleteId, onClose }) {
  const qc = useQueryClient();
  const { data: d } = useQuery({ queryKey: ["club-athlete", athleteId], queryFn: async () => (await api.get(`/api/club/athlete/${athleteId}`)).data, enabled: !!athleteId });
  const [note, setNote] = useState("");
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/api/club/athlete/${athleteId}/note`, { text: note }); setNote(""); qc.invalidateQueries({ queryKey: ["club-athlete", athleteId] }); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={!!athleteId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface border-[#1E222A] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{d?.athlete.name || "Athlete"}</DialogTitle></DialogHeader>
        {!d ? <p className="mono-label animate-pulse">Loading…</p> : (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2 text-xs">
              {d.athlete.prefs.goal && <span className="mono-label rounded-full border border-acid/50 text-acid px-2.5 py-1">goal: {d.athlete.prefs.goal.replace("_", " ")}</span>}
              {d.athlete.prefs.days_per_week && <span className="mono-label rounded-full border border-[#343A4A] px-2.5 py-1">{d.athlete.prefs.days_per_week} days/week</span>}
              {d.athlete.prefs.injuries && <span className="mono-label rounded-full border border-[#FFB340]/60 text-[#FFB340] px-2.5 py-1">limits: {d.athlete.prefs.injuries}</span>}
              <span className="mono-label rounded-full border border-[#343A4A] px-2.5 py-1">joined {d.athlete.joined || "–"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-[#1E222A] bg-[#0B0D11] p-3 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={d.profile.radar} outerRadius="70%">
                    <PolarGrid stroke="#1E222A" /><PolarAngleAxis dataKey="pattern" tick={{ fill: "#B7BECB", fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar dataKey="score" stroke="#CCFF00" fill="#CCFF00" fillOpacity={0.3} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <Mini label="consistency" value={`${d.profile.consistency}%`} />
                  <Mini label="attendance" value={`${d.attendance_rate}%`} />
                  <Mini label="last trained" value={d.cycle.days_since_last == null ? "–" : `${d.cycle.days_since_last}d ago`} />
                </div>
                <div className="rounded-lg border border-[#1E222A] bg-[#0B0D11] p-3 text-sm"><p className="mono-label text-acid mb-1">cycle · {d.cycle.phase}</p>{d.cycle.advice}</div>
                {d.profile.weak_points.length > 0 && (
                  <div className="rounded-lg border border-[#FFB340]/40 p-3 text-sm"><p className="mono-label text-[#FFB340] mb-1">weak points</p>{d.profile.weak_points.map((w) => w.pattern).join(", ")}</div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="mono-label mb-2 flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> best lifts</p>
                <div className="space-y-1 text-sm">{d.prs.slice(0, 6).map((p) => <div key={p.movement} className="flex justify-between"><span>{p.movement}</span><span className="text-acid">{p.best_est_1rm ? `${p.best_est_1rm}kg e1RM` : p.best_weight_kg ? `${p.best_weight_kg}kg` : p.best_score || "–"}</span></div>)}{!d.prs.length && <p className="text-[#565C6F]">No lifts logged yet.</p>}</div>
              </div>
              <div>
                <p className="mono-label mb-2">club classes</p>
                <div className="space-y-1 text-sm max-h-40 overflow-y-auto">{d.classes.map((c, i) => <div key={i} className="flex justify-between"><span className="truncate">{c.date} · {c.title}</span><span className={c.attended === true ? "text-acid" : c.attended === false ? "text-[#FF6B6B]" : "text-[#8A91A6]"}>{c.attended === true ? "came" : c.attended === false ? "no-show" : "booked"}</span></div>)}{!d.classes.length && <p className="text-[#565C6F]">No club classes yet.</p>}</div>
              </div>
            </div>
            <div>
              <p className="mono-label mb-2 flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> coach notes (only your club's coaches see these)</p>
              <div className="flex gap-2"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Tight hips, keep squat depth controlled" className="flex-1 h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" /><Button onClick={addNote} className="bg-acid text-black">Add</Button></div>
              <div className="space-y-1.5 mt-3">{d.notes.map((n) => <div key={n.id} className="text-sm rounded-lg bg-[#0B0D11] border border-[#1E222A] p-2.5"><span className="mono-label">{n.created_at.slice(0, 10)} · {n.author}</span><p>{n.text}</p></div>)}</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Mini({ label, value }) {
  return <div className="rounded-lg border border-[#1E222A] bg-[#0B0D11] p-2.5 text-center"><p className="font-heading font-black text-lg">{value}</p><p className="mono-label">{label}</p></div>;
}
